import React from "react";
import { fontSizes, radius } from "./tokens";
import {
  kpiTokens as baseKpiTokens,
  neutralToken,
  severityTokens as baseSeverityTokens,
  statusTokens as baseStatusTokens,
  toneTokens,
} from "./statusTokens";

const normalizeTokenMap = (source) =>
  Object.entries(source || {}).reduce((acc, [key, token]) => {
    const normalizedKey = String(key || "").trim().toLowerCase();
    if (!normalizedKey) return acc;
    acc[normalizedKey] = token;
    return acc;
  }, {});

const statusMap = {
  ...normalizeTokenMap(baseStatusTokens),
  active: { label: "Active", tone: "accent" },
  inactive: { label: "Inactive", tone: "neutral" },
  pending: { label: "Pending", tone: "neutral" },
  quarantined: { label: "Quarantined", tone: "danger" },
  archived: { label: "Archived", tone: "neutral" },
  draft: { label: "Draft", tone: "info" },
  approved: { label: "Approved", tone: "success" },
  published: { label: "Published", tone: "accent" },
  "in review": { label: "In Review", tone: "warning" },
  "within appetite": { label: "Within Appetite", tone: "success" },
  "exceeds appetite": { label: "Exceeds Appetite", tone: "danger" },
  on_track: { label: "On Track", tone: "success" },
  "on track": { label: "On Track", tone: "success" },
  "due soon": { label: "Due Soon", tone: "warning" },
  upcoming: { label: "Upcoming", tone: "accent" },
  suspended: { label: "Suspended", tone: "suspended" },
  paused: { label: "Paused", tone: "neutral" },
  effective: { label: "Effective", tone: "success" },
  "needs improvement": { label: "Partially Effective", tone: "warning" },
  needs_improvement: { label: "Partially Effective", tone: "warning" },
  ineffective: { label: "Ineffective", tone: "danger" },
  "not effective": { label: "Ineffective", tone: "danger" },
  not_effective: { label: "Ineffective", tone: "danger" },
};

const severityMap = normalizeTokenMap(baseSeverityTokens);

const kpiMap = normalizeTokenMap(baseKpiTokens);

function StatusBadge({ value, variant = "status", fallbackLabel = "Unknown", label, style, customToken, ...rest }) {
  const map = variant === "severity" ? severityMap : variant === "kpi" ? kpiMap : statusMap;
  const key = typeof value === "string" ? value.trim().toLowerCase() : "";
  const token = customToken || (key && map[key] ? map[key] : null);
  const resolved = token || {
    ...neutralToken,
    label: fallbackLabel || neutralToken.label,
    ariaLabel: `Unknown ${variant}${value ? `: ${value}` : ""}`,
  };
  const resolvedTone = toneTokens[resolved.tone] || toneTokens.neutral;
  const resolvedLabel = label || resolved.label;
  const ariaLabel =
    resolved.ariaLabel ||
    `${variant === "severity" ? "Severity" : "Status"} ${resolvedLabel}`;

  return (
    <span
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 22,
        padding: "3px 10px",
        borderRadius: radius.pill,
        border: `1px solid ${resolved.border || resolvedTone.border}`,
        background: resolved.background || resolvedTone.background,
        color: resolved.color || resolvedTone.color,
        fontSize: fontSizes.sm,
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {resolvedLabel}
    </span>
  );
}

export default StatusBadge;
